import { useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import SearchableSelect from "../../Common/SearchableSelect";
import { FaSpinner } from "react-icons/fa";
import toast from "react-hot-toast";
import * as B2BRegistrationService from "../../Services/B2BRegistration/B2BRegistrationService";
import { useSelector } from "react-redux";

const AddDoctorModal = ({ showModal, setShowModal, pincode, orgId, orgName, locationId, locationName, onDoctorAdded, onPincodeChange }) => {
    const [loading, setLoading] = useState(false);
    const [pincodeAddress, setPincodeAddress] = useState("");
    const [allSpecData, setAllSpecData] = useState([]);
    const [specialisationOptions, setSpecialisationOptions] = useState([]);
    const [subSpecialisationOptions, setSubSpecialisationOptions] = useState([]);
    const [isSubSpecDisabled, setIsSubSpecDisabled] = useState(true);
    const [fetchedDoctorDetails, setFetchedDoctorDetails] = useState(null);
    const pincodeDataRef = useRef(null);
    const [submitError, setSubmitError] = useState("");

    const partnerEmail = useSelector((state) => state.userData?.EmailId);
    const partnerMobile = useSelector((state) => state.userData?.MobileNo);

    const {
        register,
        handleSubmit,
        control,
        watch,
        reset,
        setValue,
        getValues,
        trigger,
        formState: { errors },
    } = useForm({ defaultValues: { salutation: "Dr." } });

    // Load specialization list once
    useEffect(() => {
        B2BRegistrationService.GetSpecializationList()
            .then(({ data }) => {
                if (data?.IsSuccess && data.Data?.length > 0) {
                    setAllSpecData(data.Data);

                    // Unique specializations for the dropdown
                    const seen = new Set();
                    const unique = data.Data
                        .filter((item) => {
                            if (seen.has(item.Specialization_Id)) return false;
                            seen.add(item.Specialization_Id);
                            return true;
                        })
                        .map((item) => ({ value: item.Specialization_Id, label: item.Specialization }));
                    setSpecialisationOptions(unique);
                }
            })
            .catch(() => { });
    }, []);

    useEffect(() => {
        if (!showModal) return;

        reset({ salutation: "Dr.", pincode: pincode || "", emailId: partnerEmail || "", mobileNo: partnerMobile || "" });
        setPincodeAddress("");
        setSubSpecialisationOptions([]);
        setIsSubSpecDisabled(true);
        setSubmitError("");
        pincodeDataRef.current = null;

        if (pincode && pincode.length === 6) {
            B2BRegistrationService.GetPincodeStateDetails(pincode)
                .then(({ data }) => {
                    if (data?.IsSuccess) {
                        pincodeDataRef.current = data.Data;
                        const { city, state, country } = data.Data;
                        setPincodeAddress(`${city} (${pincode}), ${state}, ${country}`);
                    }
                })
                .catch(() => { });
        }
    }, [showModal]);


    // const isSequentialDigits = (digits) => {
    //     let ascending = true;
    //     let descending = true;

    //     for (let i = 1; i < digits.length; i++) {
    //         const prev = digits.charCodeAt(i - 1) - 48;
    //         const curr = digits.charCodeAt(i) - 48;

    //         if (curr !== prev + 1) ascending = false;
    //         if (curr !== prev - 1) descending = false;
    //     }

    //     return ascending || descending;
    // };

    // const validateRegistrationNumber = (value) => {
    //     // if (!value) return true; // "required" rule already covers empty

    //     const trimmed = value.trim();

    //     if (trimmed.length < 6 || trimmed.length > 16) {
    //         return "Registration number must be between 6 and 16 characters.";
    //     }

    //     if (!/^[A-Za-z0-9]+$/.test(trimmed)) {
    //         return "Registration number can only contain letters and numbers.";
    //     }

    //     const hasDigit = /\d/.test(trimmed);
    //     const hasLetter = /[A-Za-z]/.test(trimmed);

    //     if (!hasLetter) {
    //         return "Registration number cannot contain digits only.";
    //     }
    //     if (!hasDigit) {
    //         return "Registration number must contain at least one digit.";
    //     }

    //     const digitsOnly = trimmed.replace(/[^0-9]/g, "");

    //     if (digitsOnly.length > 0 && new Set(digitsOnly).size === 1) {
    //         return "Registration number cannot be a repeated digit pattern.";
    //     }
    //     if (digitsOnly.length >= 5 && isSequentialDigits(digitsOnly)) {
    //         return "Registration number cannot be a sequential digit pattern.";
    //     }

    //     return true;
    // };
    const hasExcessiveRepeatingDigits = (value, maxAllowedRepeat = 4) => {
        let run = 1;
        for (let i = 1; i < value.length; i++) {
            const prevChar = value[i - 1];
            const currChar = value[i];
            if (/\d/.test(currChar) && currChar === prevChar) {
                run++;
                if (run > maxAllowedRepeat) return true;
            } else {
                run = 1;
            }
        }
        return false;
    };

    const hasExcessiveSequentialDigits = (value, maxAllowedSequential = 6) => {
        let ascRun = 1;
        let descRun = 1;

        for (let i = 1; i < value.length; i++) {
            const prevChar = value[i - 1];
            const currChar = value[i];

            if (/\d/.test(prevChar) && /\d/.test(currChar)) {
                const prev = prevChar.charCodeAt(0) - 48;
                const curr = currChar.charCodeAt(0) - 48;

                ascRun = curr === prev + 1 ? ascRun + 1 : 1;
                descRun = curr === prev - 1 ? descRun + 1 : 1;

                if (ascRun > maxAllowedSequential || descRun > maxAllowedSequential) {
                    return true;
                }
            } else {
                ascRun = 1;
                descRun = 1;
            }
        }
        return false;
    };

    const validateRegistrationNumber = (value) => {
        if (!value) return "Registration number is required.";

        const trimmed = value.trim();

        if (trimmed.length < 6 || trimmed.length > 16) {
            return "Registration number must be between 6 and 16 characters.";
        }

        // Pure letters, pure digits, or a mix — no special characters
        if (!/^[A-Za-z0-9]+$/.test(trimmed)) {
            return "Registration number can only contain letters and numbers.";
        }

        if (hasExcessiveRepeatingDigits(trimmed, 4)) {
            return "Registration number cannot contain more than 4 repeating digits in a row.";
        }

        if (hasExcessiveSequentialDigits(trimmed, 6)) {
            return "Registration number cannot contain a sequential digit pattern longer than 6 digits.";
        }

        return true;
    };

    const handleSpecialisationChange = (option, fieldOnChange) => {
        fieldOnChange(option);
        setValue("subSpecialisation", null);

        if (!option) {
            setSubSpecialisationOptions([]);
            setIsSubSpecDisabled(true);
            return;
        }

        const matched = allSpecData
            .filter((item) => item.Specialization_Id === option.value)
            .map((item) => ({ value: item.Sub_Specialization_Id, label: item.Sub_Specialization }));

        setSubSpecialisationOptions(matched);

        // Auto-select and disable
        if (matched.length > 0) {
            setValue("subSpecialisation", matched[0]);
        }
        setIsSubSpecDisabled(true);
    };

    const handlePincodeBlur = async () => {
        const isValid = await trigger("pincode");
        if (!isValid) {
            setPincodeAddress("");
            pincodeDataRef.current = null;
            return;
        }
        if (pincodeDataRef.current) {
            const { city, state, country } = pincodeDataRef.current;
            const currentPincode = watch("pincode");
            setPincodeAddress(`${city} (${currentPincode}), ${state}, ${country}`);
            if (onPincodeChange) onPincodeChange(currentPincode);
        }
    };

    const isAutoFilled = !!fetchedDoctorDetails?.firstName;

    const handleRegistrationBlur = () => {
        const regNo = getValues("registrationNumber");
        if (!regNo || regNo.length < 6) return;

        // var value = validateRegistrationNumber(regNo);
        // if (value == "true") {
        B2BRegistrationService.GetDoctorDetailsByRegistrationNo(regNo)
            .then(({ data }) => {
                if (data?.IsSuccess && data.Data) {
                    setFetchedDoctorDetails(data.Data.Doctor_Response_Json?.DoctorDetails || {});
                } else {
                    setFetchedDoctorDetails({});
                }
            })
            .catch(() => { setFetchedDoctorDetails({}); });
        // }
    };

    useEffect(() => {
        if (fetchedDoctorDetails === null) return;

        setValue("firstName", fetchedDoctorDetails.firstName || "");
        setValue("lastName", fetchedDoctorDetails.lastName || "");
        setValue("gender", fetchedDoctorDetails.gender || "");

        const specOption = fetchedDoctorDetails.specialization
            ? specialisationOptions.find(
                (opt) => opt.label?.toLowerCase() === fetchedDoctorDetails.specialization.toLowerCase()
            )
            : null;

        if (specOption) {
            handleSpecialisationChange(specOption, (val) => setValue("specialisation", val));

            const subSpecs = allSpecData
                .filter((item) => item.Specialization_Id === specOption.value)
                .map((item) => ({ value: item.Sub_Specialization_Id, label: item.Sub_Specialization }));
            const subSpecOption = subSpecs.find(
                (opt) => opt.label?.toLowerCase() === fetchedDoctorDetails.subSpecialization?.toLowerCase()
            );
            if (subSpecOption) {
                setValue("subSpecialisation", subSpecOption);
            }
        } else {
            setValue("specialisation", null);
            setValue("subSpecialisation", null);
            setSubSpecialisationOptions([]);
            setIsSubSpecDisabled(true);
        }
    }, [fetchedDoctorDetails]);

    const onSubmit = (data) => {
        if (isAutoFilled) {
            toast.success("Doctor selected successfully.");
            if (onDoctorAdded) {
                onDoctorAdded({
                    Doctor_Name: `${data.firstName} ${data.lastName}`,
                    RNI_Doctor_Id: fetchedDoctorDetails?.doctorId || null,
                    Doctor_Id: fetchedDoctorDetails?.doctorId || null,
                });
            }
            resetModal();
            setShowModal(false);
            return;
        }

        setLoading(true);
        setSubmitError("");

        const payload = {
            Salutation: "Dr.",
            FirstName: data.firstName,
            LastName: data.lastName,
            Gender: data.gender,
            Specialisation: data.specialisation?.label || "",
            SpecialisationId: data.specialisation?.value || null,
            SubSpecialisation: data.subSpecialisation?.label || "",
            SubSpecialisationId: data.subSpecialisation?.value || null,
            RegistrationNumber: data.registrationNumber,
            EmailId: data.emailId,
            MobileNo: data.mobileNo,
            Pincode: data.pincode,
            OrgId: orgId,
            OrgName: orgName,
            LocationId: locationId,
            LocationName: locationName
        };

        B2BRegistrationService.CreateDoctor(payload)
            .then(({ data: res }) => {
                if (res.IsSuccess) {
                    toast.success(res.ResponseMessage || "Doctor added successfully.");
                    resetModal();
                    setShowModal(false);
                    if (onDoctorAdded) {
                        onDoctorAdded({
                            Doctor_Name: `${payload.FirstName} ${payload.LastName}`,
                            RNI_Doctor_Id: res.Data?.RNI_Doctor_Id || null,
                            Doctor_Id: res.Data?.Doctor_Id || null,
                        });
                    }
                } else {
                    const msg = res.ResponseMessage || "Failed to add doctor.";
                    toast.error(msg);
                    setSubmitError(msg);
                }
            })
            .catch(() => toast.error("Something went wrong."))
            .finally(() => setLoading(false));
    };

    const resetModal = () => {
        reset({ salutation: "Dr.", emailId: partnerEmail || "", mobileNo: partnerMobile || "" });
        setPincodeAddress("");
        setSubmitError("");
        pincodeDataRef.current = null;
        setSubSpecialisationOptions([]);
        setIsSubSpecDisabled(true);
        setFetchedDoctorDetails(null);
    };

    const handleClose = () => {
        resetModal();
        setShowModal(false);
    };

    if (!showModal) return null;

    return (
        <div>
            <div className="modal-backdrop" style={{ zIndex: 10000 }}>
                <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ zIndex: 10001 }}>
                    <div className="modal-dialog modal-md custom-modal-com" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Add Doctor</h5>
                                <button type="button" className="btn-close" onClick={handleClose}></button>
                            </div>

                            <form onSubmit={handleSubmit(onSubmit)}>
                                <div className="modal-body" style={{ maxHeight: "65vh", overflowY: "auto" }}>
                                    {submitError && (
                                        <div className="alert alert-danger py-2 px-3 mb-2" role="alert">
                                            {submitError}
                                        </div>
                                    )}
                                    <div className="row g-2">

                                        {/* Registration Number */}
                                        <div className="col-12 col-md-6 mb-1">
                                            <label className="fw-bold required mb-1">Registration Number</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Enter registration number"
                                                maxLength={16}
                                                {...register("registrationNumber", {
                                                    required: "Registration number is required",
                                                    validate: validateRegistrationNumber,
                                                    onChange: (e) => {
                                                        if (submitError) setSubmitError("");
                                                        if (!e.target.value) {
                                                            setFetchedDoctorDetails(null);
                                                            setValue("firstName", "");
                                                            setValue("lastName", "");
                                                            setValue("gender", "");
                                                            setValue("specialisation", null);
                                                            setValue("subSpecialisation", null);
                                                            setSubSpecialisationOptions([]);
                                                            setIsSubSpecDisabled(true);
                                                        }
                                                    }
                                                })}
                                                onBlur={handleRegistrationBlur}
                                                onInput={(e) => {
                                                    e.target.value = e.target.value.replace(/[^A-Za-z0-9]/g, "");
                                                }}
                                            />
                                            {errors.registrationNumber && <p className="text-danger">{errors.registrationNumber.message}</p>}
                                        </div>

                                        {/* Salutation — hardcoded Dr. */}
                                        <div className="col-12 col-md-6 mb-1">
                                            <label className="fw-bold required mb-1">Salutation</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                {...register("salutation")}
                                                disabled
                                            />
                                        </div>

                                        {/* Gender */}
                                        <div className="col-12 col-md-6 mb-1">
                                            <label className="fw-bold required mb-1">Gender</label>
                                            <select
                                                className="form-select"
                                                disabled={isAutoFilled}
                                                {...register("gender", { required: "Gender is required" })}
                                            >
                                                <option value="">Select</option>
                                                <option value="Female">Female</option>
                                                <option value="Male">Male</option>
                                                <option value="NA">NA</option>
                                                <option value="Transgender">Transgender</option>
                                            </select>
                                            {errors.gender && <p className="text-danger">{errors.gender.message}</p>}
                                        </div>

                                        {/* First Name */}
                                        <div className="col-12 col-md-6 mb-1">
                                            <label className="fw-bold required mb-1">First Name</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Enter first name"
                                                disabled={isAutoFilled}
                                                {...register("firstName", {
                                                    required: "First name is required",
                                                    pattern: { value: /^[A-Za-z\s]+$/, message: "Only letters are allowed" }
                                                })}
                                                onInput={(e) => {
                                                    e.target.value = e.target.value.replace(/[^A-Za-z\s]/g, "");
                                                    const val = e.target.value;
                                                    e.target.value = val.charAt(0).toUpperCase() + val.slice(1);
                                                }}
                                            />
                                            {errors.firstName && <p className="text-danger">{errors.firstName.message}</p>}
                                        </div>

                                        {/* Last Name */}
                                        <div className="col-12 col-md-6 mb-1">
                                            <label className="fw-bold required mb-1">Last Name</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Enter last name"
                                                disabled={isAutoFilled}
                                                {...register("lastName", {
                                                    required: "Last name is required",
                                                    pattern: { value: /^[A-Za-z.\s]+$/, message: "Only letters are allowed" }
                                                })}
                                                onInput={(e) => {
                                                    e.target.value = e.target.value.replace(/[^A-Za-z.\s]/g, "");
                                                    const val = e.target.value;
                                                    e.target.value = val.charAt(0).toUpperCase() + val.slice(1);
                                                }}
                                            />
                                            {errors.lastName && <p className="text-danger">{errors.lastName.message}</p>}
                                        </div>

                                        {/* Specialisation */}
                                        <div className="col-12 col-md-6 mb-1">
                                            <label className="fw-bold required mb-1">Specialisation</label>
                                            <Controller
                                                name="specialisation"
                                                control={control}
                                                rules={{ required: "Specialisation is required" }}
                                                render={({ field }) => (
                                                    <SearchableSelect
                                                        options={specialisationOptions}
                                                        placeholder="Select"
                                                        isDisabled={isAutoFilled}
                                                        value={field.value}
                                                        onChange={(option) => handleSpecialisationChange(option, field.onChange)}
                                                        onBlur={field.onBlur}
                                                    />
                                                )}
                                            />
                                            {errors.specialisation && <p className="text-danger">{errors.specialisation.message}</p>}
                                        </div>

                                        {/* Sub-Specialisation — auto-filled and disabled based on specialisation */}
                                        <div className="col-12 col-md-6 mb-1">
                                            <label className="fw-bold mb-1">Sub-Specialisation</label>
                                            <Controller
                                                name="subSpecialisation"
                                                control={control}
                                                // rules={{ required: "Sub-Specialisation is required" }}
                                                render={({ field }) => (
                                                    <SearchableSelect
                                                        options={subSpecialisationOptions}
                                                        placeholder="Select Sub-Specialisation"
                                                        isDisabled={isAutoFilled || isSubSpecDisabled}
                                                        value={field.value}
                                                        onChange={(option) => field.onChange(option)}
                                                        onBlur={field.onBlur}
                                                    />
                                                )}
                                            />
                                            {errors.subSpecialisation && <p className="text-danger">{errors.subSpecialisation.message}</p>}
                                        </div>

                                        {/* Email ID */}
                                        <div className="col-12 col-md-6 mb-1">
                                            <label className="fw-bold required mb-1">Email ID</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Enter email address"
                                                disabled={isAutoFilled}
                                                {...register("emailId", {
                                                    required: "Email is required",
                                                    pattern: {
                                                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                                        message: "Invalid email address",
                                                    },
                                                })}
                                            />
                                            {errors.emailId && <p className="text-danger">{errors.emailId.message}</p>}
                                        </div>

                                        {/* Mobile No */}
                                        <div className="col-12 col-md-6 mb-1">
                                            <label className="fw-bold required mb-1">Mobile No</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Enter mobile number"
                                                maxLength={10}
                                                disabled={isAutoFilled}
                                                {...register("mobileNo", {
                                                    required: "Mobile number is required",
                                                    pattern: {
                                                        value: /^[0-9]{10}$/,
                                                        message: "Mobile number must be 10 digits",
                                                    },
                                                })}
                                            />
                                            {errors.mobileNo && <p className="text-danger">{errors.mobileNo.message}</p>}
                                        </div>

                                        {/* Pincode */}
                                        <div className="col-12 col-md-6 mb-1">
                                            <label className="fw-bold required mb-1">Pincode</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Enter pincode"
                                                maxLength={6}
                                                disabled={isAutoFilled}
                                                {...register("pincode", {
                                                    required: "Pincode is required",
                                                    validate: async (value) => {
                                                        if (!value) return true;
                                                        if (value.length !== 6) return "Pincode must be 6 digits";
                                                        try {
                                                            const { data } = await B2BRegistrationService.GetPincodeStateDetails(value);
                                                            if (data?.IsSuccess) {
                                                                pincodeDataRef.current = data.Data;
                                                                return true;
                                                            }
                                                            pincodeDataRef.current = null;
                                                            return "Invalid Pincode";
                                                        } catch {
                                                            pincodeDataRef.current = null;
                                                            return "Invalid Pincode";
                                                        }
                                                    },
                                                    pattern: { value: /^[0-9]*$/, message: "Only numbers allowed" },
                                                })}
                                                onInput={(e) => {
                                                    e.target.value = e.target.value.replace(/\D/g, "");
                                                }}
                                                onBlur={handlePincodeBlur}
                                            />
                                            {errors.pincode && <p className="text-danger">{errors.pincode.message}</p>}
                                        </div>

                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-danger"
                                        tabIndex={-1}
                                        onClick={handleClose}
                                    >
                                        Close
                                    </button>
                                    <button type="submit" className="btn custom-sumbitbtn" disabled={loading}>
                                        {loading ? <FaSpinner className="fa-spin mb-1" /> : "Save"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddDoctorModal;
